import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDate } from '../../utils/date';
import {
  getAdminTimesheets, getUsers, seedTestTimesheets, deleteTimesheetDay,
  getDayTimeEntries, adminCreateTimeEntry, adminUpdateTimeEntry, adminDeleteTimeEntry,
  adminRetryBilling,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin,
  AlertTriangle, Building2, Download, Trash2, Sparkles, Pencil, Plus, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import { isoDate as ISO } from '../../lib/dateUtils';

// ───────────────────── Helpers ─────────────────────
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  // Monday = 0 in our week shape (UK ops convention)
  const diff = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};
const fmtHours = (h) => (h || 0).toFixed(2);
const dayLabel = (iso) => formatDate(iso);
const dayLabelShort = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const isoToShortDate = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
};

// Simplified: 4 named units. Stepping </> with each one snaps to a
// full calendar-aligned range (week = Mon-Sun, month = 1st → last,
// quarter = Jan-Mar | Apr-Jun | Jul-Sep | Oct-Dec, year = 1 Jan → 31 Dec).
const RANGE_PRESETS = [
  { value: 'week', label: 'Week (Mon–Sun)' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

// Sentinel for the "any customer / any engineer" option. Shadcn's Select
// refuses an empty string value so we use a stable marker instead.
const ANY = '__any__';

// Builds a calendar-aligned range for the given unit anchored on `ref`
// (defaults to today). All four units return exact 1st-day → last-day
// windows so the `<` / `>` arrows always land on clean boundaries.
const rangeForUnit = (unit, ref) => {
  const r = ref ? new Date(ref) : new Date();
  r.setHours(0, 0, 0, 0);
  if (unit === 'week') {
    const from = startOfWeek(r);                // Monday
    return { from, to: addDays(from, 6) };
  }
  if (unit === 'month') {
    const from = new Date(r.getFullYear(), r.getMonth(), 1);
    const to = new Date(r.getFullYear(), r.getMonth() + 1, 0);
    return { from, to };
  }
  if (unit === 'quarter') {
    const q = Math.floor(r.getMonth() / 3); // 0..3
    const from = new Date(r.getFullYear(), q * 3, 1);
    const to = new Date(r.getFullYear(), q * 3 + 3, 0);
    return { from, to };
  }
  if (unit === 'year') {
    const from = new Date(r.getFullYear(), 0, 1);
    const to = new Date(r.getFullYear(), 11, 31);
    return { from, to };
  }
  return { from: r, to: r };
};

// CSV export — flat one-row-per-day layout matching the on-screen
// calendar, so the admin can drop straight into payroll.
const buildCsv = (data) => {
  const rows = [
    [
      'Engineer', 'Date',
      'In (Lutrom)', 'In (customer)', 'Out (customer)', 'Out (Lutrom)',
      'Day total hours (Lutrom span)',
      'On-customer hours',
      'WO logged hours',
      'Admin-authored',
    ],
  ];
  data.engineers.forEach((eng) => {
    eng.days.forEach((day) => {
      const s = day.summary || {};
      rows.push([
        eng.name,
        day.date,
        s.lutromIn ? fmtTime(s.lutromIn) : '',
        s.customerIn ? fmtTime(s.customerIn) : '',
        s.customerOut ? fmtTime(s.customerOut) : '',
        s.lutromOut ? fmtTime(s.lutromOut) : '',
        s.lutromSpanHours != null ? fmtHours(s.lutromSpanHours) : '',
        s.customerSpanHours != null ? fmtHours(s.customerSpanHours) : '',
        fmtHours(s.woLoggedHours || 0),
        // Explicit "yes" / "" so the CSV opens cleanly in Excel and
        // payroll can filter on the column at a glance.
        s.adminAuthored ? 'yes' : '',
      ]);
    });
  });
  return rows.map(r =>
    r.map(cell => {
      const s = String(cell ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    }).join(',')
  ).join('\n');
};

const downloadCsv = (data) => {
  const csv = buildCsv(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `timesheets_${data.from}_to_${data.to}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ───────────────────── Page ─────────────────────
const Timesheets = () => {
  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(() => rangeForUnit('month'));
  const [customerId, setCustomerId] = useState(ANY);
  const [engineerId, setEngineerId] = useState(ANY);
  // Populate the filter dropdowns. Customers list is primary
  // (no parentCustomerId) so siblings don't double up.
  const [allUsers, setAllUsers] = useState([]);
  const customers = useMemo(
    () => allUsers.filter(u => u.role === 'customer' && !u.parentCustomerId)
      .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '')),
    [allUsers],
  );
  const engineers = useMemo(
    () => allUsers.filter(u => u.role === 'employee' || u.role === 'admin')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allUsers],
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  // Admin-only: which day is currently being edited via the dialog?
  // `null` means the dialog is closed.
  const [editTarget, setEditTarget] = useState(null);
  // Floating "+ Add time entry" picker — quick way to spawn the day
  // editor for any engineer/date without scrolling the calendar grid.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // When the admin taps a day cell while no engineer is selected, we
  // open the quick-add dialog prefilled with that date so they don't
  // have to re-pick the calendar after choosing an engineer.
  const [quickAddPrefillDate, setQuickAddPrefillDate] = useState(null);

  // Alias — used by the DayEditDialog callback for readability.
  // `refresh` is defined below (useCallback); arrow-function wrapper
  // avoids the TDZ issue from referencing it before its declaration.
  const refreshTimesheet = () => refresh();

  useEffect(() => {
    getUsers().then(setAllUsers).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const params = {
        from: ISO(range.from),
        to: ISO(range.to),
      };
      if (customerId !== ANY) params.customerId = customerId;
      if (engineerId !== ANY) params.userIds = engineerId;
      const res = await getAdminTimesheets(params);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [range, customerId, engineerId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Seed: populate the currently-visible month (or the month containing
  // `range.from` if the window spans multiple) with synthetic weekday
  // entries for the chosen engineer (or Marcus by default). Idempotent —
  // already-seeded days are skipped by the backend.
  const handleSeed = async () => {
    if (!range) return;
    const baseDate = range.from;
    const body = {
      year: baseDate.getFullYear(),
      month: baseDate.getMonth() + 1,
    };
    if (engineerId !== ANY) body.userId = engineerId;
    setSeeding(true);
    try {
      const res = await seedTestTimesheets(body);
      toast.success(
        `Seeded ${res.seededEntries} entries + ${res.seededLabor} WO labour rows for ${res.userName}` +
        (res.seededEntries === 0 ? ' (already seeded — nothing to do)' : ''),
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to seed');
    } finally {
      setSeeding(false);
    }
  };

  const handleDeleteDay = async (userIdToDelete, date, engineerName) => {
    if (!window.confirm(`Delete all clock-ins + WO labour for ${engineerName} on ${date}? This cannot be undone.`)) return;
    try {
      const res = await deleteTimesheetDay(userIdToDelete, date);
      toast.success(`Cleared ${res.entriesRemoved} entries + ${res.laborRemoved} WO labour rows`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Always show every calendar day in the active range so the grid looks
  // identical regardless of which filters are set. Admins explicitly
  // asked for this: "no matter what I select, it will look exactly the
  // same whether I select a customer and no engineer or no customer and
  // no engineers." Blank days can then be tapped to add entries.
  const filterActive = false;

  const onPreset = (v) => {
    setPreset(v);
    setRange(rangeForUnit(v));
  };

  // Step the active range by ±1 calendar unit. Using the midpoint of the
  // current range as the anchor keeps us aligned on boundary-perfect
  // windows even if the user manually tweaked the dates (which nudges
  // the range.from slightly before / after the unit's real start).
  const step = (dir) => {
    const mid = new Date(range.from);
    mid.setHours(12, 0, 0, 0);
    if (preset === 'week') {
      mid.setDate(mid.getDate() + dir * 7);
    } else if (preset === 'month') {
      mid.setMonth(mid.getMonth() + dir);
    } else if (preset === 'quarter') {
      mid.setMonth(mid.getMonth() + dir * 3);
    } else if (preset === 'year') {
      mid.setFullYear(mid.getFullYear() + dir);
    }
    setRange(rangeForUnit(preset, mid));
  };

  const totalAcrossEngineers = useMemo(() => {
    if (!data) return 0;
    return data.engineers.reduce((acc, e) => acc + (e.totalHours || 0), 0);
  }, [data]);

  // Range totals rolled up across every engineer — powers the overview
  // tiles at the top (Lutrom span, on-customer span, WO logged).
  const rangeTotals = useMemo(() => {
    const t = { lutromSpan: 0, customerSpan: 0, woLogged: 0, daysWithData: 0, flagged: 0 };
    if (!data) return t;
    data.engineers.forEach((eng) => {
      eng.days.forEach((d) => {
        const s = d.summary || {};
        t.lutromSpan += s.lutromSpanHours || 0;
        t.customerSpan += s.customerSpanHours || 0;
        t.woLogged += s.woLoggedHours || 0;
        if ((d.entries?.length || 0) > 0 || (s.woLoggedHours || 0) > 0) t.daysWithData += 1;
        if (d.totalHours > 12 || d.entries.some((e) => e.outsideGeofence)) t.flagged += 1;
      });
    });
    return t;
  }, [data]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Timesheets"
        description="Calendar of every engineer's clocked hours, with the customer they were on site for."
      />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3" data-testid="timesheets-filters">
        <div className="flex-1 min-w-[140px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Range</label>
          <Select value={preset} onValueChange={onPreset}>
            <SelectTrigger className="h-9" data-testid="timesheets-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`timesheets-range-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Customer</label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="h-9" data-testid="timesheets-customer-select">
              <SelectValue placeholder="Any customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} data-testid="timesheets-customer-any">Any customer</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`timesheets-customer-${c.id}`}>
                  {c.company || c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Engineer</label>
          <Select value={engineerId} onValueChange={setEngineerId}>
            <SelectTrigger className="h-9" data-testid="timesheets-engineer-select">
              <SelectValue placeholder="Any engineer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} data-testid="timesheets-engineer-any">Any engineer</SelectItem>
              {engineers.map(e => (
                <SelectItem key={e.id} value={e.id} data-testid={`timesheets-engineer-${e.id}`}>
                  {e.name} {e.role === 'admin' && <span className="text-[10px] uppercase text-amber-700 ml-1">admin</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1 ml-auto">
          <Button size="sm" variant="outline" onClick={() => step(-1)} aria-label="Previous range" data-testid="timesheets-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => step(1)} aria-label="Next range" data-testid="timesheets-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSeed}
            disabled={seeding}
            className="text-violet-700 border-violet-300 hover:bg-violet-50"
            data-testid="timesheets-seed-test"
            title="Populate the visible range with synthetic weekday entries for a test engineer"
          >
            <Sparkles className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">{seeding ? 'Seeding…' : 'Seed test data'}</span>
          </Button>
          <Button
            size="sm"
            onClick={() => data && downloadCsv(data)}
            disabled={!data}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="timesheets-export-csv"
          >
            <Download className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Header summary line */}
      {data && (
        <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
          <CalendarIcon className="h-4 w-4 text-slate-400" />
          <span>
            <strong className="text-slate-900">{data.engineers.length}</strong> engineer{data.engineers.length === 1 ? '' : 's'} ·{' '}
            <strong className="text-slate-900">{fmtHours(totalAcrossEngineers)}</strong> hours ·{' '}
            <span className="text-slate-500">
              {dayLabel(data.from)} → {dayLabel(data.to)}
            </span>
          </span>
        </div>
      )}

      {/* Overview totals — rolls every engineer's days into a 4-tile
          summary so payroll / finance can scan the range at a glance
          before drilling into the per-engineer grid below. */}
      {data && <RangeTotalsCard totals={rangeTotals} />}

      {/* Calendar grid */}
      {loading && !data && <div className="text-slate-500">Loading…</div>}
      {data && data.engineers.length === 0 && engineerId === ANY && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm text-blue-900" data-testid="timesheets-empty-pick-engineer">
            <span className="font-semibold">No clocked entries in this range.</span>{' '}
            Tap any day below to add time — you'll pick the engineer in the next step.
          </div>
          <EngineerSection
            engineer={{
              id: '__no_engineer_picked__',
              name: 'All engineers',
              role: 'employee',
              totalHours: 0,
              days: (data.days || []).map(d => ({
                date: d,
                totalHours: 0,
                entries: [],
                summary: {
                  lutromIn: null, customerIn: null, customerOut: null, lutromOut: null,
                  lutromSpanHours: null, customerSpanHours: null, woLoggedHours: 0,
                },
              })),
            }}
            dates={data.days}
            filterActive={false}
            onDeleteDay={handleDeleteDay}
            onEditDay={(_engId, _engName, date) => {
              // No engineer selected yet → route through the quick-add
              // dialog prefilled with the tapped date.
              setQuickAddPrefillDate(date);
              setQuickAddOpen(true);
            }}
          />
        </>
      )}
      {data && data.engineers.length === 0 && engineerId !== ANY && (
        <EngineerSection
          engineer={{
            id: engineerId,
            name: engineers.find(e => e.id === engineerId)?.name || 'Engineer',
            role: engineers.find(e => e.id === engineerId)?.role || 'employee',
            totalHours: 0,
            days: (data.days || []).map(d => ({
              date: d,
              totalHours: 0,
              entries: [],
              summary: {
                lutromIn: null, customerIn: null, customerOut: null, lutromOut: null,
                lutromSpanHours: null, customerSpanHours: null, woLoggedHours: 0,
              },
            })),
          }}
          dates={data.days}
          filterActive={false}
          onDeleteDay={handleDeleteDay}
          onEditDay={(engId, engName, date) => setEditTarget({ engineerId: engId, engineerName: engName, date })}
        />
      )}
      {data && data.engineers.map(eng => (
        <EngineerSection
          key={eng.id}
          engineer={eng}
          dates={data.days}
          filterActive={filterActive}
          onDeleteDay={handleDeleteDay}
          onEditDay={(engId, engName, date) => setEditTarget({ engineerId: engId, engineerName: engName, date })}
        />
      ))}

      {/* Admin-only day-edit dialog — opens when admin taps a day card
          on mobile. Lets them add, edit, or delete each lane's
          clock-in / clock-out times directly. */}
      <DayEditDialog
        target={editTarget}
        onClose={(refresh) => {
          setEditTarget(null);
          if (refresh) refreshTimesheet();
        }}
      />

      {/* Floating "+ Add time entry" button — admin shortcut to open
          the day editor for any engineer/date pair without scrolling
          the grid. Hidden when the day editor is already open or while
          the page is loading the initial dataset. */}
      {!editTarget && !quickAddOpen && (
        <button
          type="button"
          onClick={() => setQuickAddOpen(true)}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 active:scale-95 transition-all text-sm font-semibold"
          data-testid="timesheets-quick-add-btn"
          aria-label="Add time entry"
        >
          <Plus className="h-4 w-4" /> Add time entry
        </button>
      )}

      <QuickAddDialog
        open={quickAddOpen}
        onOpenChange={(v) => {
          setQuickAddOpen(v);
          if (!v) setQuickAddPrefillDate(null);
        }}
        engineers={engineers}
        defaultDate={quickAddPrefillDate || ISO(range?.from || new Date())}
        onContinue={(engineer, date) => {
          setEditTarget({ engineerId: engineer.id, engineerName: engineer.name, date });
          setQuickAddOpen(false);
          setQuickAddPrefillDate(null);
        }}
      />
    </div>
  );
};

const EngineerSection = ({ engineer, dates, filterActive, onDeleteDay, onEditDay }) => {
  // Quick scan: flag days with > 12h or any outside-geofence entry as a
  // visual anomaly. Not blocking — just a heads-up so the admin doesn't
  // have to read every row.
  const anomalies = useMemo(() => engineer.days.filter(d =>
    d.totalHours > 12 || d.entries.some(e => e.outsideGeofence) || d.summary?.forgotToClockOut
  ).length, [engineer.days]);

  // Always render every day in the visible range (with em-dash
  // placeholders for empty days) — admins use empty days to backfill
  // and need every date tappable regardless of filters.
  const visibleDays = engineer.days;

  const totals = useMemo(() => {
    // Column totals across visible days for the footer row.
    let lutromSpan = 0, customerSpan = 0, woLogged = 0;
    visibleDays.forEach(d => {
      lutromSpan += d.summary?.lutromSpanHours || 0;
      customerSpan += d.summary?.customerSpanHours || 0;
      woLogged += d.summary?.woLoggedHours || 0;
    });
    return { lutromSpan, customerSpan, woLogged };
  }, [visibleDays]);

  // Header rollup: hours clocked at Lutrom HQ + a per-customer
  // breakdown so the admin can see at a glance which customers ate the
  // engineer's week before scrolling to individual days. Computes from
  // clockIn/clockOut span (matches the "On customer" / "Day total"
  // columns) so admin-authored entries with empty `hours` still count.
  const headerBreakdown = useMemo(() => {
    const span = (a, b) => {
      if (!a || !b) return 0;
      const ms = new Date(b).getTime() - new Date(a).getTime();
      return ms > 0 ? ms / 3600000 : 0;
    };
    let lutromHours = 0;
    const byCustomer = {};
    engineer.days.forEach((d) => {
      (d.entries || []).forEach((e) => {
        const h = (Number(e.hours) || 0) || span(e.clockIn, e.clockOut);
        if (h <= 0) return;
        if (e.kind === 'lutrom' || e.isLutrom) {
          lutromHours += h;
        } else if (e.customerName) {
          byCustomer[e.customerName] = (byCustomer[e.customerName] || 0) + h;
        } else {
          // Customer-lane entry without a resolved customer name (rare —
          // happens when the WO/site lookup fails). Bucket under
          // "Unassigned" so the admin sees it instead of losing the time.
          byCustomer['Unassigned'] = (byCustomer['Unassigned'] || 0) + h;
        }
      });
    });
    const customers = Object.entries(byCustomer)
      .map(([name, hours]) => ({ name, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours);
    return {
      lutromHours: Math.round(lutromHours * 100) / 100,
      customers,
      customerTotal: Math.round(customers.reduce((s, c) => s + c.hours, 0) * 100) / 100,
    };
  }, [engineer.days]);

  const isSynthetic = engineer.id === 'empty';

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`timesheet-engineer-${engineer.id}`}>
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 font-semibold flex items-center justify-center text-sm shrink-0">
            {engineer.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{engineer.name}</div>
            <div className="text-xs text-slate-500 capitalize">{engineer.role}</div>
          </div>
        </div>
        <div className="text-right shrink-0 max-w-[55%]">
          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1" data-testid={`engineer-breakdown-${engineer.id}`}>
            <div className="text-right">
              <div className="text-base font-semibold tabular-nums text-slate-900">
                {fmtHours(headerBreakdown.lutromHours)}h
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Lutrom HQ</div>
            </div>
            <div className="text-right">
              <div className="text-base font-semibold tabular-nums text-emerald-700">
                {fmtHours(headerBreakdown.customerTotal)}h
              </div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700/80">Customers</div>
            </div>
          </div>
          {headerBreakdown.customers.length > 0 && (
            <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
              {headerBreakdown.customers.map((c) => (
                <span
                  key={c.name}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800"
                  title={`${c.name} · ${fmtHours(c.hours)}h`}
                  data-testid={`engineer-customer-pill-${engineer.id}`}
                >
                  <span className="font-medium truncate max-w-[140px]">{c.name}</span>
                  <span className="tabular-nums font-semibold">{fmtHours(c.hours)}h</span>
                </span>
              ))}
            </div>
          )}
          {anomalies > 0 && (
            <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1 justify-end">
              <AlertTriangle className="h-3 w-3" /> {anomalies} flagged
            </div>
          )}
        </div>
      </div>

      {/* Desktop: 9-column calendar. The three column groups are separated
          with a slightly darker border so the eye reads "day span @
          Lutrom", "customer span", "WO breakdown" as discrete sections. */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-52">Date</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">In (Lutrom)</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-l border-slate-200">In (customer)</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Out (customer)</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-r border-slate-200">Out (Lutrom)</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Day total (Lutrom)</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">On customer</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">WO logged</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right w-16" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleDays.map(d => (
              <CalendarDayRow
                key={d.date}
                day={d}
                onDelete={
                  isSynthetic || !onDeleteDay
                    ? null
                    : () => onDeleteDay(engineer.id, d.date, engineer.name)
                }
              />
            ))}
          </tbody>
          {visibleDays.length > 0 && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td className="px-3 py-2 font-semibold text-slate-700" colSpan={5}>Range totals</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{fmtHours(totals.lutromSpan)}h</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{fmtHours(totals.customerSpan)}h</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{fmtHours(totals.woLogged)}h</td>
                <td className="px-3 py-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Mobile: one card per day with the Lutrom / Customer boxes
          side-by-side and a trash icon tucked in the header. Keeps
          all the data visible without a side-scroll on a phone. */}
      <ul className="lg:hidden divide-y divide-slate-100" data-testid={`timesheet-mobile-${engineer.id}`}>
        {visibleDays.map(d => (
          <CalendarDayMobile
            key={d.date}
            day={d}
            engineerId={engineer.id}
            engineerName={engineer.name}
            isSynthetic={isSynthetic}
            onDelete={
              isSynthetic || !onDeleteDay
                ? null
                : () => onDeleteDay(engineer.id, d.date, engineer.name)
            }
            onEdit={isSynthetic ? null : onEditDay}
          />
        ))}
      </ul>
    </div>
  );
};

const T = ({ iso, fallback = '—' }) => (
  <span className="tabular-nums">{iso ? fmtTime(iso) : <span className="text-slate-300">{fallback}</span>}</span>
);
const H = ({ value, zero = '—' }) => (
  <span className="tabular-nums">
    {value && value > 0 ? `${fmtHours(value)}h` : <span className="text-slate-300">{zero}</span>}
  </span>
);

/** One calendar row: date + 4 clock-in/out cells + 3 hour-total cells +
 *  a delete button. Zero / missing values render as muted em-dashes so
 *  the eye can scan straight down each column for gaps. */
const CalendarDayRow = ({ day, onDelete }) => {
  const hasData = day.entries.length > 0 || (day.summary?.woLoggedHours || 0) > 0;
  const s = day.summary || {};
  return (
    <tr
      className={hasData ? 'hover:bg-slate-50' : 'text-slate-400'}
      data-testid={`timesheet-day-${day.date}`}
    >
      <td className="px-3 py-2 text-slate-700 font-medium">
        <div className="flex items-center gap-1.5 flex-wrap">
          {dayLabel(day.date)}
          {s.adminAuthored && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold px-1 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200"
              title="One or more entries on this day were added or edited by an admin"
              data-testid={`admin-authored-badge-desktop-${day.date}`}
            >
              <Pencil className="h-2 w-2" /> Admin
            </span>
          )}
          {s.forgotToClockOut && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold px-1 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
              title="Engineer self-closed a forgotten clock-out — review the claimed time"
              data-testid={`forgot-clockout-badge-desktop-${day.date}`}
            >
              <AlertTriangle className="h-2 w-2" /> Self-closed
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2"><T iso={s.lutromIn} /></td>
      <td className="px-3 py-2 border-l border-slate-100"><T iso={s.customerIn} /></td>
      <td className="px-3 py-2"><T iso={s.customerOut} /></td>
      <td className="px-3 py-2 border-r border-slate-100"><T iso={s.lutromOut} /></td>
      <td className="px-3 py-2 text-right"><H value={s.lutromSpanHours} /></td>
      <td className="px-3 py-2 text-right"><H value={s.customerSpanHours} /></td>
      <td className="px-3 py-2 text-right font-semibold text-slate-900"><H value={s.woLoggedHours} /></td>
      <td className="px-3 py-2 text-right">
        {hasData && onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
            title={`Delete all data for ${day.date}`}
            data-testid={`timesheet-day-delete-${day.date}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </td>
    </tr>
  );
};

const CalendarDayMobile = ({ day, engineerId, engineerName, isSynthetic, onDelete, onEdit }) => {
  const s = day.summary || {};
  const hasData = day.entries.length > 0 || (s.woLoggedHours || 0) > 0;
  // `onEdit` opens the admin day-edit dialog. Synthetic placeholder
  // engineer rows can't be edited (no real userId to post against).
  const canEdit = !isSynthetic && !!onEdit;
  return (
    <li
      className="px-4 py-3 space-y-2"
      data-testid={`timesheet-mobile-day-${day.date}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className={`font-medium flex items-center gap-1.5 flex-wrap ${hasData ? 'text-slate-900' : 'text-slate-400'}`}>
          {dayLabel(day.date)}
          {s.adminAuthored && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold px-1 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200"
              title="One or more entries on this day were added or edited by an admin"
              data-testid={`admin-authored-badge-${day.date}`}
            >
              <Pencil className="h-2 w-2" /> Admin
            </span>
          )}
          {s.forgotToClockOut && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold px-1 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
              title="Engineer self-closed a forgotten clock-out — review the claimed time"
              data-testid={`forgot-clockout-badge-${day.date}`}
            >
              <AlertTriangle className="h-2 w-2" /> Self-closed
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className={`text-sm font-semibold tabular-nums ${hasData ? 'text-slate-900' : 'text-slate-400'}`}>
            {fmtHours(s.woLoggedHours || 0)}h WO
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => onEdit(engineerId, engineerName, day.date)}
              className="text-slate-400 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition-colors"
              title={hasData ? 'Edit entries' : 'Add entries'}
              data-testid={`timesheet-mobile-day-edit-${day.date}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {hasData && onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
              title={`Delete all data for ${day.date}`}
              data-testid={`timesheet-mobile-day-delete-${day.date}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
      {/* Every day always renders the two lane boxes. Empty days just
          show em-dashes — gives the admin a consistent tappable target
          to drop entries onto without hunting for a "+" button. */}
      <div
        className={`grid grid-cols-2 gap-2 text-xs ${canEdit ? 'cursor-pointer active:opacity-70' : ''}`}
        onClick={canEdit ? () => onEdit(engineerId, engineerName, day.date) : undefined}
      >
        <div className="bg-slate-50 border border-slate-200 rounded p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Lutrom</div>
          <div className="flex justify-between"><span className="text-slate-500">In</span><T iso={s.lutromIn} /></div>
          <div className="flex justify-between"><span className="text-slate-500">Out</span><T iso={s.lutromOut} /></div>
          <div className="flex justify-between pt-1 mt-1 border-t border-slate-200 font-semibold"><span>Total</span><H value={s.lutromSpanHours} /></div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Customer</div>
          <div className="flex justify-between"><span className="text-slate-500">In</span><T iso={s.customerIn} /></div>
          <div className="flex justify-between"><span className="text-slate-500">Out</span><T iso={s.customerOut} /></div>
          <div className="flex justify-between pt-1 mt-1 border-t border-slate-200 font-semibold"><span>On site</span><H value={s.customerSpanHours} /></div>
        </div>
      </div>
    </li>
  );
};

/** Rolled-up totals banner shown directly above the per-engineer grid.
 *  4 tiles: Clocked at Lutrom, On customer site, WO logged, Days with
 *  data. Updates live as the admin steps through ranges. */
const RangeTotalsCard = ({ totals }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="timesheets-range-totals">
    <TotalTile
      icon={Clock}
      label="On the clock"
      value={`${fmtHours(totals.lutromSpan)}h`}
      tone="bg-slate-900 border-slate-900 text-white"
      testId="range-total-lutrom"
    />
    <TotalTile
      icon={Building2}
      label="On customer"
      value={`${fmtHours(totals.customerSpan)}h`}
      tone="bg-emerald-50 border-emerald-200 text-emerald-900"
      testId="range-total-customer"
    />
    <TotalTile
      icon={MapPin}
      label="WO logged"
      value={`${fmtHours(totals.woLogged)}h`}
      tone="bg-blue-50 border-blue-200 text-blue-900"
      testId="range-total-wo"
    />
    <TotalTile
      icon={CalendarIcon}
      label="Days with data"
      value={`${totals.daysWithData}`}
      tone={totals.flagged > 0
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-slate-50 border-slate-200 text-slate-900'}
      subLabel={totals.flagged > 0 ? `${totals.flagged} flagged` : null}
      testId="range-total-days"
    />
  </div>
);

const TotalTile = ({ icon: Icon, label, value, tone, subLabel, testId }) => (
  <div className={`rounded-md border p-3 sm:p-4 ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 opacity-80 text-[10px] uppercase tracking-wider">
      <Icon className="h-3 w-3" />
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-1 font-display text-2xl sm:text-3xl tabular-nums leading-tight">{value}</div>
    {subLabel && (
      <div className="text-[10px] mt-0.5 flex items-center gap-1 opacity-90">
        <AlertTriangle className="h-2.5 w-2.5" /> {subLabel}
      </div>
    )}
  </div>
);

// ───────────────────── Admin day-edit dialog ─────────────────────
// Opens when an admin taps the pencil / box on a day card. Lists every
// existing entry for that (engineer, date) and lets the admin create /
// edit / delete any of them. Uses type="time" inputs and splices the
// date back on submit so the payload always carries a full ISO datetime.
const DayEditDialog = ({ target, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirtyIds, setDirtyIds] = useState({}); // id -> true when edited locally

  useEffect(() => {
    if (!target) return;
    setLoading(true);
    setDirtyIds({});
    Promise.all([
      getDayTimeEntries(target.engineerId, target.date),
      getUsers().catch(() => []),
    ])
      .then(([rows, users]) => {
        setEntries((rows || []).map((r) => ({ ...r })));
        // Filter users → top-level customer accounts only (sub-customers
        // have `parentCustomerId`).
        setCustomers((users || []).filter((u) => u.role === 'customer' && !u.parentCustomerId));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load entries'))
      .finally(() => setLoading(false));
  }, [target]);

  if (!target) return null;

  const updateLocal = (id, patch) => {
    setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setDirtyIds((d) => ({ ...d, [id]: true }));
  };

  // Splits "HH:MM" out of an ISO datetime string so <input type="time">
  // Convert a UTC ISO timestamp to local "HH:mm" for the
  // <input type="time"> field. Without this the admin sees 05:00
  // when the engineer clocked in at 06:00 BST — because the
  // backend stores UTC. Round-trips via `toIso` so the user-typed
  // local time is converted back to UTC for storage.
  const toTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };
  // Build a UTC ISO from `target.date` at the typed LOCAL time.
  // Constructing via `new Date(y, mo, da, h, m)` honours the
  // browser's TZ offset, then `.toISOString()` returns UTC.
  const toIso = (hhmm) => {
    if (!hhmm || !target.date) return '';
    const [h, m] = hhmm.split(':').map(Number);
    const [y, mo, da] = target.date.split('-').map(Number);
    const d = new Date(y, mo - 1, da, h, m, 0, 0);
    return isNaN(d.getTime()) ? '' : d.toISOString();
  };

  const addLane = (kind) => {
    // Sensible defaults so the user doesn't have to type four times —
    // Lutrom mirrors an 08:00-17:00 shift, Customer 09:30-16:30.
    const defaults = kind === 'lutrom'
      ? { clockIn: '08:00', clockOut: '17:00' }
      : { clockIn: '09:30', clockOut: '16:30' };
    const tempId = `__new_${Math.random().toString(36).slice(2, 8)}`;
    setEntries((es) => [
      ...es,
      {
        id: tempId,
        isNew: true,
        userId: target.engineerId,
        userName: target.engineerName,
        kind,
        date: target.date,
        clockIn: toIso(defaults.clockIn),
        clockOut: toIso(defaults.clockOut),
        breakMins: 0,
        customerId: '',
        workOrderId: '',
        notes: '',
      },
    ]);
    setDirtyIds((d) => ({ ...d, [tempId]: true }));
  };

  const removeEntry = async (entry) => {
    if (entry.isNew) {
      setEntries((es) => es.filter((e) => e.id !== entry.id));
      return;
    }
    if (!window.confirm('Delete this clock entry? This cannot be undone.')) return;
    try {
      await adminDeleteTimeEntry(entry.id);
      setEntries((es) => es.filter((e) => e.id !== entry.id));
      toast.success('Entry deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const saveAll = async () => {
    // Enforce customer selection on the customer lane — without it the
    // backend skips draft-invoice accumulation ("no customer on entry")
    // and the admin thinks their save did nothing. Fail fast with a
    // clear toast instead.
    for (const e of entries) {
      if (!dirtyIds[e.id]) continue;
      if (e.kind === 'customer' && !e.customerId) {
        toast.error('Pick a customer for the customer-lane entry before saving');
        return;
      }
    }
    setSaving(true);
    const billResults = []; // accumulate for the summary toast
    const skipReasons = []; // customer entries that should have billed but didn't
    try {
      for (const entry of entries) {
        if (!dirtyIds[entry.id]) continue;
        if (!entry.clockIn || !entry.clockOut) {
          toast.error(`Missing clock-in/out on ${entry.kind} lane`);
          setSaving(false);
          return;
        }
        const base = {
          kind: entry.kind,
          clockIn: entry.clockIn,
          clockOut: entry.clockOut,
          breakMins: Number(entry.breakMins || 0),
          customerId: entry.customerId || '',
          workOrderId: entry.workOrderId || '',
          notes: entry.notes || '',
        };
        let res;
        if (entry.isNew) {
          res = await adminCreateTimeEntry({
            ...base,
            userId: entry.userId,
            date: entry.date,
          });
        } else {
          res = await adminUpdateTimeEntry(entry.id, base);
        }
        if (res && res.billedInvoiceId) {
          billResults.push({
            kind: entry.kind,
            amount: res.billedAmount,
            invoiceId: res.billedInvoiceId,
          });
        } else if (res && entry.kind === 'customer' && res.billSkipReason) {
          skipReasons.push(res.billSkipReason);
        }
      }
      // Summary toast: tell the admin what just landed on a draft so
      // they don't have to go hunting for it. Surface skip reasons for
      // customer entries that didn't bill (e.g. no rate rule matched).
      if (billResults.length > 0) {
        const total = billResults.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        toast.success(
          `Saved · £${total.toFixed(2)} added to draft ${billResults[0].invoiceId}`,
        );
      } else if (skipReasons.length > 0) {
        toast.warning(`Saved but not billed — ${skipReasons[0]}`);
      } else {
        toast.success('Entries saved');
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="day-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-700" />
            {target.engineerName} &middot; {dayLabel(target.date)}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading entries…</div>
        ) : (
          <div className="space-y-3">
            {entries.length === 0 && (
              <p className="text-sm text-slate-500 italic">No clock entries yet. Add one below.</p>
            )}
            {entries.map((e) => (
              <div
                key={e.id}
                className="rounded border border-slate-200 p-3 space-y-2"
                data-testid={`entry-row-${e.isNew ? 'new' : e.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-sm font-semibold capitalize">
                    {e.kind === 'lutrom' ? <Home className="h-3.5 w-3.5 text-blue-700" /> : <Building2 className="h-3.5 w-3.5 text-emerald-700" />}
                    {e.kind}
                    {e.adminOverride && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200"
                        title={e.adminAuthorName ? `Added by ${e.adminAuthorName}` : 'Admin-authored entry'}
                        data-testid={`entry-admin-badge-${e.isNew ? 'new' : e.id}`}
                      >
                        <Pencil className="h-2 w-2" /> Admin
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEntry(e)}
                    className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                    title="Remove entry"
                    data-testid={`entry-delete-${e.isNew ? 'new' : e.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs">
                    <span className="text-slate-500">Clock in</span>
                    <Input
                      type="time"
                      className="mt-1 h-9"
                      value={toTime(e.clockIn)}
                      onChange={(ev) => updateLocal(e.id, { clockIn: toIso(ev.target.value) })}
                      data-testid={`entry-clock-in-${e.isNew ? 'new' : e.id}`}
                    />
                  </label>
                  <label className="text-xs">
                    <span className="text-slate-500">Clock out</span>
                    <Input
                      type="time"
                      className="mt-1 h-9"
                      value={toTime(e.clockOut)}
                      onChange={(ev) => updateLocal(e.id, { clockOut: toIso(ev.target.value) })}
                      data-testid={`entry-clock-out-${e.isNew ? 'new' : e.id}`}
                    />
                  </label>
                </div>

                {e.kind === 'customer' && (
                  <div>
                    <label className="text-xs">
                      <span className="text-slate-500">
                        Customer <span className="text-red-600">*</span>
                      </span>
                      <Select
                        value={e.customerId || ''}
                        onValueChange={(v) => updateLocal(e.id, { customerId: v })}
                      >
                        <SelectTrigger
                          className={`mt-1 h-9 ${!e.customerId ? 'border-red-300' : ''}`}
                          data-testid={`entry-customer-${e.isNew ? 'new' : e.id}`}
                        >
                          <SelectValue placeholder="Required — pick a customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.company || c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    {!e.customerId && (
                      <p className="text-[10px] text-red-600 mt-0.5">
                        Needed so hours can be billed to the customer&rsquo;s draft invoice.
                      </p>
                    )}
                  </div>
                )}

                <label className="text-xs block">
                  <span className="text-slate-500">Notes</span>
                  <Input
                    className="mt-1 h-9"
                    value={e.notes || ''}
                    onChange={(ev) => updateLocal(e.id, { notes: ev.target.value })}
                    placeholder="Optional"
                    data-testid={`entry-notes-${e.isNew ? 'new' : e.id}`}
                  />
                </label>

                {/* Billing status panel — surfaces auto-billing outcome
                    so the admin knows whether this clock-out landed on
                    a draft invoice, or why it didn't. */}
                {!e.isNew && e.kind === 'customer' && (
                  e.billedInvoiceId ? (
                    <div
                      className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-900 flex items-center gap-1.5"
                      data-testid={`entry-billed-chip-${e.id}`}
                    >
                      <span className="font-semibold">Billed</span>
                      <span className="text-emerald-700">→ {e.billedInvoiceId}</span>
                      {typeof e.billedLabourTotal === 'number' && (
                        <span className="text-emerald-700">· £{Number(e.billedLabourTotal).toFixed(2)}</span>
                      )}
                    </div>
                  ) : e.billSkipReason ? (
                    <div
                      className="rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 space-y-1.5"
                      data-testid={`entry-skip-chip-${e.id}`}
                    >
                      <div className="flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold">Not billed yet</div>
                          <div className="text-amber-800">{e.billSkipReason}</div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-amber-300 hover:bg-amber-100 w-full"
                        data-testid={`entry-retry-bill-${e.id}`}
                        onClick={async () => {
                          try {
                            const res = await adminRetryBilling(e.id);
                            if (res?.ok && res.invoiceId) {
                              toast.success(
                                `Billed · £${Number(res.amountAdded || 0).toFixed(2)} added to draft ${res.invoiceId}`,
                              );
                              setEntries((es) => es.map((it) => (
                                it.id === e.id
                                  ? { ...it, billedInvoiceId: res.invoiceId,
                                      billedLabourTotal: res.amountAdded,
                                      billSkipReason: undefined }
                                  : it
                              )));
                            } else {
                              toast.warning(`Still not billed — ${res?.reason || 'unknown reason'}`);
                              setEntries((es) => es.map((it) => (
                                it.id === e.id
                                  ? { ...it, billSkipReason: res?.reason || it.billSkipReason }
                                  : it
                              )));
                            }
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Retry failed');
                          }
                        }}
                      >
                        Retry billing
                      </Button>
                    </div>
                  ) : null
                )}
              </div>
            ))}

            {/* Add-lane buttons — pre-fill sensible defaults so the
                admin only has to tweak the times if the default shift
                doesn't fit. */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addLane('lutrom')}
                data-testid="add-lutrom-lane"
                className="flex-1"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Lutrom lane
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addLane('customer')}
                data-testid="add-customer-lane"
                className="flex-1"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Customer lane
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={saveAll}
            disabled={saving || loading}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="day-edit-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Lightweight picker that fronts the floating "+ Add time entry" CTA.
// Two fields only — engineer + date — then hands off to the existing
// DayEditDialog so the admin gets the full lane / break / customer UI
// without us duplicating any logic.
const QuickAddDialog = ({ open, onOpenChange, engineers, defaultDate, onContinue }) => {
  const todayIso = ISO(new Date());
  const [engineerId, setEngineerId] = useState('');
  const [date, setDate] = useState(defaultDate || todayIso);

  useEffect(() => {
    if (open) {
      setEngineerId('');
      setDate(defaultDate || todayIso);
    }
  }, [open, defaultDate, todayIso]);

  const selectedEngineer = engineers.find((e) => e.id === engineerId);

  const handleContinue = () => {
    if (!selectedEngineer) {
      toast.error('Pick an engineer first');
      return;
    }
    if (!date) {
      toast.error('Pick a date');
      return;
    }
    onContinue(selectedEngineer, date);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle>Add time entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">
              Engineer
            </label>
            <Select value={engineerId} onValueChange={setEngineerId}>
              <SelectTrigger className="h-10" data-testid="quick-add-engineer-select">
                <SelectValue placeholder="Select an engineer…" />
              </SelectTrigger>
              <SelectContent>
                {engineers.map((e) => (
                  <SelectItem
                    key={e.id}
                    value={e.id}
                    data-testid={`quick-add-engineer-${e.id}`}
                  >
                    {e.name} <span className="text-slate-400 ml-1 capitalize">· {e.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">
              Date
            </label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10"
              data-testid="quick-add-date"
            />
          </div>
          <p className="text-xs text-slate-500">
            Continuing opens the day editor where you can add Lutrom &amp; customer clock-in / clock-out times.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleContinue}
            disabled={!engineerId || !date}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="quick-add-continue"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Timesheets;
